'use client';
import React, { useState, useEffect } from 'react';
import { Droplets, Layers, Sprout, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { ancSetService, AncSet } from '@/lib/services/ancService';
import Icon from '@/components/ui/AppIcon';


const TIER_ITEMS = [
  { tier: 'top', label: 'Top', sub: 'Food & new bedding', icon: Sprout, colour: 'text-green-600', bg: 'bg-green-100', border: 'border-green-200' },
  { tier: 'middle', label: 'Middle', sub: 'ANC bedding & castings', icon: Layers, colour: 'text-amber-600', bg: 'bg-amber-100', border: 'border-amber-200' },
  { tier: 'bottom', label: 'Bottom', sub: 'Water / leachate', icon: Droplets, colour: 'text-blue-600', bg: 'bg-blue-100', border: 'border-blue-200' },
];

function SetCard({ set }: { set: AncSet }) {
  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="px-3 py-2.5 border-b border-border flex items-center justify-between">
        <div>
          <p className="text-sm font-bold text-foreground">{set.setName}</p>
          {set.location && <p className="text-xs text-muted-foreground">{set.location}</p>}
        </div>
        <span className="text-xs font-semibold text-success bg-success-bg px-2 py-0.5 rounded-full">Active</span>
      </div>
      <div className="divide-y divide-border">
        {TIER_ITEMS.map((t) => {
          const Icon = t.icon;
          return (
            <div key={t.tier} className="flex items-center gap-3 px-3 py-2.5">
              <div className={`w-7 h-7 rounded-lg ${t.bg} ${t.border} border flex items-center justify-center flex-shrink-0`}>
                <Icon size={14} className={t.colour} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-semibold ${t.colour}`}>{t.label} Tier</p>
                <p className="text-xs text-muted-foreground leading-tight">{t.sub}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AncSetOverview() {
  const { user } = useAuth();
  const [sets, setSets] = useState<AncSet[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newSetName, setNewSetName] = useState('');
  const [newLocation, setNewLocation] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    ancSetService.getAll().then((data) => { setSets(data); setLoading(false); });
  }, []);

  const handleAdd = async () => {
    if (!newSetName.trim()) { toast.error('Set name required'); return; }
    if (!user) return;
    setSaving(true);
    const result = await ancSetService.create({ setName: newSetName.trim(), location: newLocation.trim() || undefined, createdBy: user.id });
    if (result) {
      setSets((prev) => [...prev, result]);
      setNewSetName('');
      setNewLocation('');
      setShowAdd(false);
      toast.success('ANC set added');
    } else {
      toast.error('Failed to add set');
    }
    setSaving(false);
  };

  return (
    <div className="bg-card rounded-xl border border-border shadow-sm">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-foreground">ANC Sets</h2>
          <p className="text-xs text-muted-foreground">{sets.length} active sets · 3 tiers each</p>
        </div>
        <button
          onClick={() => setShowAdd((v) => !v)}
          className="flex items-center gap-1 text-xs font-semibold text-primary px-2 py-1.5 rounded-lg active:bg-muted transition-colors"
        >
          <Plus size={13} />
          Add Set
        </button>
      </div>

      {showAdd && (
        <div className="px-4 py-3 border-b border-border bg-muted/30 space-y-2">
          <input
            type="text"
            value={newSetName}
            onChange={(e) => setNewSetName(e.target.value)}
            placeholder="Set name (e.g. ANC Set 05)"
            className="w-full rounded-xl border border-border px-3 py-2.5 text-sm bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <input
            type="text"
            value={newLocation}
            onChange={(e) => setNewLocation(e.target.value)}
            placeholder="Location (optional)"
            className="w-full rounded-xl border border-border px-3 py-2.5 text-sm bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <div className="flex gap-2">
            <button
              onClick={handleAdd}
              disabled={saving}
              className="flex-1 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-bold disabled:opacity-70 flex items-center justify-center gap-1"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : null}
              Save
            </button>
            <button onClick={() => setShowAdd(false)} className="flex-1 py-2.5 rounded-xl border border-border text-sm font-semibold text-foreground">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="p-3">
        {loading ? (
          <div className="py-6 flex items-center justify-center">
            <Loader2 size={20} className="animate-spin text-muted-foreground" />
          </div>
        ) : sets.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">No ANC sets yet. Add your first set above.</p>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {sets.map((s) => <SetCard key={s.id} set={s} />)}
          </div>
        )}
      </div>
    </div>
  );
}
